import { useQuery } from '@tanstack/react-query';

import api from '@/axios';
import { GET_PURCHASE_ORDER_LINES_KEY } from '@/keys';
import type { Paginated } from '@/types/core/paginated';
import type { PurchaseOrderProduct } from '@/types/core/purchase-order/purchase-order-product';
import type { DataTableQueryVariables } from '@/types/data-table';

type GetPurchaseOrderLinesParams = Pick<
  DataTableQueryVariables<PurchaseOrderProduct>,
  'page' | 'perPage'
>;

type PurchaseOrderLinesResponse =
  | Paginated<PurchaseOrderProduct>
  | PurchaseOrderProduct[];

function isPaginatedPurchaseOrderLinesResponse(
  response: PurchaseOrderLinesResponse,
): response is Paginated<PurchaseOrderProduct> {
  return !Array.isArray(response);
}

export const getPurchaseOrderLines = async (
  id: string,
  { page = 1, perPage = 10 }: GetPurchaseOrderLinesParams,
): Promise<Paginated<PurchaseOrderProduct>> => {
  const response = await api.get<PurchaseOrderLinesResponse>(
    `/purchase-orders/${id}/lines`,
    {
      params: {
        page,
        perPage,
      },
    },
  );

  if (isPaginatedPurchaseOrderLinesResponse(response.data)) {
    return response.data;
  }

  const total = response.data.length;
  const maxPage = Math.ceil(total / perPage) || 1;
  const currentPage = Math.min(page, maxPage);
  const start = (currentPage - 1) * perPage;

  return {
    data: response.data.slice(start, start + perPage),
    currentPage,
    maxPage,
    total,
    perPage,
  };
};

export const useGetPurchaseOrderLines = ({
  id,
  page,
  perPage,
}: GetPurchaseOrderLinesParams & { id: string }) =>
  useQuery({
    queryKey: [...GET_PURCHASE_ORDER_LINES_KEY, id, page, perPage],
    queryFn: () => getPurchaseOrderLines(id, { page, perPage }),
    enabled: Boolean(id),
  });
